import { Box, Typography, useTheme } from '@mui/material';
import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react';
import ImageUploader from '../ImageUploader/ImageUploader';
import ColorPicker from '../ColorPicker/ColorPicker';
import CustomFontUploader from '../CustomFontUploader/CustomFontUploader';
import { IDesign } from '../../shared/interfaces/IDesign';
import { useTranslation } from 'react-i18next';

interface Props {
  defaultDesign: IDesign;
  onChange: Dispatch<SetStateAction<IDesign>>;
}

const TrustpageDesignChanger: FC<Props> = ({ defaultDesign, onChange }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [design, setDesign] = useState<IDesign>(defaultDesign);

  const updateDesign = useCallback((key: keyof IDesign, value: string | File) => {
    setDesign((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleLogoChange = () => (logoFile: File) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const base64Logo = event.target?.result as string;

      setDesign((prevDesign) => ({
        ...prevDesign,
        companyLogo: base64Logo,
      }));
    };
    reader.readAsDataURL(logoFile);
  };

  const handleColorChange = useCallback(
    (colorKey: 'primaryColor' | 'secondaryColor') => (colorValue: string) => {
      updateDesign(colorKey, colorValue);
    },
    [updateDesign]
  );

  const handleFontChange = useCallback(
    (fontFile: File) => {
      const reader = new FileReader();
      const extension = fontFile.name.split('.').pop()?.toLowerCase() || '';

      reader.onload = (event) => {
        const base64Font = event.target?.result as string;

        setDesign((prevDesign) => ({
          ...prevDesign,
          customFont: base64Font,
          customFontFormat: extension,
        }));
      };

      reader.readAsDataURL(fontFile);
    },
    [updateDesign]
  );

  useEffect(() => {
    onChange(design);
  }, [design, onChange]);

  return (
    <Box
      sx={{
        display: 'flex',
        my: theme.spacing(1),
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography>{t('logo_label')}</Typography>
        <ImageUploader onChange={handleLogoChange()} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: theme.spacing(1),
          justifyContent: 'space-between',
        }}
      >
        <Typography>{t('primary_color_label')}</Typography>
        <ColorPicker
          defaultValue={design.primaryColor}
          onChange={handleColorChange('primaryColor')}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: theme.spacing(1),
          justifyContent: 'space-between',
        }}
      >
        <Typography>{t('secondary_color_label')}</Typography>
        <ColorPicker
          defaultValue={design.secondaryColor}
          onChange={handleColorChange('secondaryColor')}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: theme.spacing(1),
          justifyContent: 'space-between',
        }}
      >
        <Typography>{t('font')}</Typography>
        <CustomFontUploader onChange={handleFontChange} />{' '}
      </Box>
    </Box>
  );
};

export default TrustpageDesignChanger;
